class TabSystem {
  constructor(section) {
    this.section = section;
    this.MOBILE_BREAKPOINT = 767;
    this.lastScrollTop = 0;
    this.isInitialized = false;
    
    this.elements = this.cacheElements();
    
    this.handleTabClick = this.handleTabClick.bind(this);
    this.handleDropdownToggle = this.handleDropdownToggle.bind(this);
    this.handleDropdownItemClick = this.handleDropdownItemClick.bind(this);
    this.handleOutsideClick = this.handleOutsideClick.bind(this);
    this.handleMobileScroll = this.debounce(this.handleMobileScroll.bind(this), 10);
    
    this.init();
  }

  cacheElements() {
    if (!this.section) {
      throw new Error('Tab section element is required');
    }

    return {
      tabHeader: this.section.querySelector('.tab-header'),
      tabLinks: Array.from(this.section.querySelectorAll('.tab-link')),
      tabContents: Array.from(this.section.querySelectorAll('.tab-content')),
      dropdownButton: this.section.querySelector('.tab__dropdown-button'),
      dropdownList: this.section.querySelector('.tab__dropdown-list'),
      dropdownItems: Array.from(this.section.querySelectorAll('.tab__dropdown-item')),
      dropdownText: this.section.querySelector('.tab__dropdown-text')
    };
  }

  getTabHeaderOffset() {
    const styles = getComputedStyle(document.documentElement);
    const announcementHeight = parseInt(styles.getPropertyValue('--announcement-height')) || 0;
    const headerHeight = parseInt(styles.getPropertyValue('--header-height')) || 0;
     const gfpbarHeight = parseInt(styles.getPropertyValue('--go-proBar-height')) || 0;
    const stickyAtcHeader = document.querySelector('.sticky-atc-header');
    const stickyAtcHeaderHeight = stickyAtcHeader?.offsetHeight || 0;
    
    return announcementHeight + headerHeight + stickyAtcHeaderHeight+gfpbarHeight;
  }

  debounce(func, wait) {
    let timeout; 
    return (...args) => {
      clearTimeout(timeout);
      timeout = setTimeout(() => func.apply(this, args), wait);
    };
  }

  isMobile() {
    return window.innerWidth <= this.MOBILE_BREAKPOINT;
  }

  handleMobileScroll() {
    if (!this.elements.tabHeader) return;

    const offset = `${this.getTabHeaderOffset()}px`;
    
    if (!this.isMobile()) {
      this.elements.tabHeader.style.top = offset;
      return;
    }

    this.elements.tabHeader.style.top = offset;
    this.lastScrollTop = window.scrollY;
  }

  setActiveTab(tabId) {
    const tab = this.section.querySelector(`.tab-content-${tabId}`);
    
    if (!tab) {
      console.warn(`Tab content not found for tabId: ${tabId}`);
      return false;
    }

    this.clearActiveStates();

    const activeTabLink = this.section.querySelector(`[data-tab="${tabId}"]`);
    if (activeTabLink) {
      activeTabLink.classList.add('current');
    }
    
    tab.classList.add('current');

    this.updateDropdown(tabId);

    this.section.dispatchEvent(new CustomEvent('theme:tab:change', {
      detail: { tabId, tab }
    }));

    this.handleCarouselResize(tab);

    return true;
  }

  clearActiveStates() {
    this.elements.tabLinks.forEach(link => link.classList.remove('current'));
    this.elements.tabContents.forEach(content => content.classList.remove('current'));
    this.elements.dropdownItems.forEach(item => item.classList.remove('is-active'));
  }

  updateDropdown(tabId) {
    if (!this.elements.dropdownButton || !this.elements.dropdownList) return;

    const selectedItem = this.elements.dropdownList.querySelector(`[data-tab="${tabId}"]`);
    
    if (selectedItem && this.elements.dropdownText) {
      this.elements.dropdownText.textContent = selectedItem.textContent;
      selectedItem.classList.add('is-active');
    }

    this.closeDropdown();
  }

  handleCarouselResize(tab) {
    const carousel = tab.querySelector('flickity-carousel');
    if (carousel?.carouselInstance) {
      requestAnimationFrame(() => {
        carousel.carouselInstance.resize();
      });
    }
  }

  handleTabClick(event) {
    event.preventDefault();
    
    try {
      const tabId = parseInt(event.currentTarget.getAttribute('data-tab'));
      this.setActiveTab(tabId);
    } catch (error) {
      console.error('Error handling tab click:', error);
    }
  }

  handleDropdownToggle(event) {
    event.preventDefault();
    event.stopPropagation();

    if (!this.elements.dropdownList || !this.elements.dropdownButton) return;

    const isVisible = this.elements.dropdownList.classList.contains('is-visible');
    
    if (isVisible) {
      this.closeDropdown();
    } else {
      this.openDropdown();
    }
  }

  openDropdown() {
    if (!this.elements.dropdownList || !this.elements.dropdownButton) return;

    this.elements.dropdownList.classList.add('is-visible');
    this.elements.dropdownButton.classList.add('is-active');
    this.elements.dropdownButton.setAttribute('aria-expanded', 'true');
  }

  closeDropdown() {
    if (!this.elements.dropdownList || !this.elements.dropdownButton) return;

    this.elements.dropdownList.classList.remove('is-visible');
    this.elements.dropdownButton.classList.remove('is-active');
    this.elements.dropdownButton.setAttribute('aria-expanded', 'false');
  }

  handleDropdownItemClick(event) {
    event.preventDefault();
    event.stopPropagation();

    const tabId = event.currentTarget.getAttribute('data-tab');
    this.setActiveTab(parseInt(tabId));
  }

  handleOutsideClick(event) {
    if (!this.elements.dropdownButton || !this.elements.dropdownList) return;

    const isClickInsideDropdown = 
      this.elements.dropdownButton.contains(event.target) || 
      this.elements.dropdownList.contains(event.target);

    if (!isClickInsideDropdown) {
      this.closeDropdown();
    }
  }

  initializeDropdown() {
    if (!this.elements.dropdownButton || !this.elements.dropdownList) return;

    const activeItem = this.elements.dropdownList.querySelector('[data-active="true"]');
    if (activeItem && this.elements.dropdownText) {
      this.elements.dropdownText.textContent = activeItem.textContent;
      activeItem.classList.add('is-active');
    }
  }

  initializeDefaultTab() {
    const firstTabLink = this.section.querySelector('.tab-link-0');
    const firstTabContent = this.section.querySelector('.tab-content-0');
    
    if (firstTabContent) firstTabContent.classList.add('current');
    if (firstTabLink) firstTabLink.classList.add('current');
  }

  attachEventListeners() {
    this.elements.tabLinks.forEach(link => {
      link.addEventListener('click', this.handleTabClick);
    });

    if (this.elements.dropdownButton) {
      this.elements.dropdownButton.addEventListener('click', this.handleDropdownToggle);
    }

    this.elements.dropdownItems.forEach(item => {
      item.addEventListener('click', this.handleDropdownItemClick);
    });

    document.addEventListener('click', this.handleOutsideClick);

    window.addEventListener('scroll', this.handleMobileScroll, { passive: true });
  }

  removeEventListeners() {
    this.elements.tabLinks.forEach(link => {
      link.removeEventListener('click', this.handleTabClick);
    });

    if (this.elements.dropdownButton) {
      this.elements.dropdownButton.removeEventListener('click', this.handleDropdownToggle);
    }

    this.elements.dropdownItems.forEach(item => {
      item.removeEventListener('click', this.handleDropdownItemClick);
    });

    document.removeEventListener('click', this.handleOutsideClick);
    window.removeEventListener('scroll', this.handleMobileScroll);
  }

  init() {
    if (this.isInitialized) return;

    if (!this.elements.tabHeader) {
      console.warn('Tab header not found, skipping initialization');
      return;
    }

    try {
      this.initializeDefaultTab();
      this.initializeDropdown();
      this.attachEventListeners();
      
      this.handleMobileScroll();
      
      this.isInitialized = true;
    } catch (error) {
      console.error('Error initializing tab system:', error);
    }
  }

  destroy() {
    if (!this.isInitialized) return;

    this.removeEventListeners();
    this.isInitialized = false;
  }

  switchToTab(tabId) {
    return this.setActiveTab(tabId);
  }

  getCurrentTab() {
    const activeTabContent = this.section.querySelector('.tab-content.current');
    if (!activeTabContent) return null;

    const classList = Array.from(activeTabContent.classList);
    const tabClass = classList.find(cls => cls.startsWith('tab-content-'));
    
    return tabClass ? parseInt(tabClass.split('-').pop()) : null;
  }

  refresh() {
    this.elements = this.cacheElements();
    this.handleMobileScroll();
  }
}

class TabManager {
  constructor() {
    this.tabSystems = new Map();
    this.init();
  }

  init() {
    document.addEventListener('DOMContentLoaded', () => {
      this.initializeAllTabSystems();
    });

    document.addEventListener('theme:section:load', (event) => {
      this.initializeTabSystem(event.detail.sectionId);
    });

    document.addEventListener('theme:section:unload', (event) => {
      this.destroyTabSystem(event.detail.sectionId);
    });
  }

  initializeAllTabSystems() {
    const sections = document.querySelectorAll('[data-tabs-holder]');
    
    sections.forEach((section, index) => {
      const sectionId = section.id || `tab-system-${index}`;
      this.initializeTabSystem(sectionId, section);
    });
  }

  initializeTabSystem(sectionId, sectionElement = null) {
    const section = sectionElement || document.getElementById(sectionId);
    
    if (!section) {
      console.warn(`Tab section not found: ${sectionId}`);
      return;
    }

    try {
      const tabSystem = new TabSystem(section);
      this.tabSystems.set(sectionId, tabSystem);
    } catch (error) {
      console.error(`Failed to initialize tab system ${sectionId}:`, error);
    }
  }

  destroyTabSystem(sectionId) {
    const tabSystem = this.tabSystems.get(sectionId);
    
    if (tabSystem) {
      tabSystem.destroy();
      this.tabSystems.delete(sectionId);
    }
  }

  getTabSystem(sectionId) {
    return this.tabSystems.get(sectionId);
  }

  destroyAll() {
    this.tabSystems.forEach(tabSystem => tabSystem.destroy());
    this.tabSystems.clear();
  }
}

const tabManager = new TabManager();